import { openDB, DBSchema, IDBPDatabase } from 'idb';

interface MediQRDB extends DBSchema {
  profiles: {
    key: string;
    value: {
      id: string;
      data: any;
      lastSync: number;
    };
  };
  pendingChanges: {
    key: string;
    value: {
      id: string;
      type: 'create' | 'update' | 'delete';
      table: string;
      data: any;
      timestamp: number;
    };
    indexes: { 'by-timestamp': number };
  };
}

let db: IDBPDatabase<MediQRDB> | null = null;

const initDB = async () => {
  if (db) return db;

  db = await openDB<MediQRDB>('mediqr-offline', 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('profiles')) {
        db.createObjectStore('profiles', { keyPath: 'id' });
      }
      
      if (!db.objectStoreNames.contains('pendingChanges')) {
        const store = db.createObjectStore('pendingChanges', { 
          keyPath: 'id',
          autoIncrement: true 
        });
        store.createIndex('by-timestamp', 'timestamp');
      }
    },
  });

  return db;
};

export const saveProfileOffline = async (profileId: string, data: any) => {
  const database = await initDB();
  await database.put('profiles', {
    id: profileId,
    data,
    lastSync: Date.now()
  });
};

export const getProfileOffline = async (profileId: string) => {
  const database = await initDB();
  return await database.get('profiles', profileId);
};

export const getAllProfilesOffline = async () => {
  const database = await initDB();
  return await database.getAll('profiles');
};

export const deleteProfileOffline = async (profileId: string) => {
  const database = await initDB();
  await database.delete('profiles', profileId);
};

export const addPendingChange = async (
  type: 'create' | 'update' | 'delete',
  table: string,
  data: any
) => {
  const database = await initDB();
  await database.add('pendingChanges', {
    id: crypto.randomUUID(),
    type,
    table,
    data,
    timestamp: Date.now()
  });
};

export const getPendingChanges = async () => {
  const database = await initDB();
  return await database.getAllFromIndex('pendingChanges', 'by-timestamp');
};

export const clearPendingChange = async (changeId: string) => {
  const database = await initDB();
  await database.delete('pendingChanges', changeId);
};

export const syncPendingChanges = async (supabase: any) => {
  const changes = await getPendingChanges();
  
  for (const change of changes) {
    try {
      switch (change.type) {
        case 'create':
          await supabase.from(change.table).insert(change.data);
          break;
        case 'update':
          await supabase.from(change.table).update(change.data).eq('id', change.data.id);
          break;
        case 'delete':
          await supabase.from(change.table).delete().eq('id', change.data.id);
          break;
      }
      
      await clearPendingChange(change.id);
    } catch (error) {
      console.error('Error syncing change:', error);
    }
  }
};

export const isOnline = () => {
  return navigator.onLine;
};

export const clearAllOfflineData = async () => {
  const database = await initDB();
  await database.clear('profiles');
  await database.clear('pendingChanges');
};
